import { useState, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import campaignService from '../services/campaignService';

/**
 * Custom hook for managing table data (companies/contacts)
 * Handles data fetching, pagination, and streaming updates
 * 
 * @param {string} campaignId - Campaign ID
 * @param {Object} companyFields - Company custom fields hook
 * @param {Object} contactFields - Contact custom fields hook
 * @param {Object} listRef - Optional ref to the virtualized list for scroll control
 * @returns {Object} - Data state and operations
 */
const useTableData = (campaignId, companyFields, contactFields, listRef = null) => {
  const [companiesData, setCompaniesData] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [isLoadingTabData, setIsLoadingTabData] = useState(false);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(false);
  const [hasMoreContacts, setHasMoreContacts] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState(null);
  // Phase 3.5 compliance sideload from the contacts response. `hasCompliance
  // Reviewers` is the master switch for the advisor-side chip UI; the map
  // is config_id -> compliance field definition id, used by
  // useAdvisorCompliance to find the decision blob on each contact.
  // Both are set together from every contacts REST response so the two
  // stay consistent.
  const [complianceFieldMap, setComplianceFieldMap] = useState({});
  const [hasComplianceReviewers, setHasComplianceReviewers] = useState(false);
  
  // Separate offsets for each tab
  const companiesOffsetRef = useRef(0);
  const contactsOffsetRef = useRef(0);
  const currentSearchRef = useRef('');
  const currentComplianceFilterRef = useRef(null);
  // Monotonically-increasing version counter bumped on every fresh
  // (non-append) contacts fetch. Pre-Plan A this guarded an eager
  // `Promise.all` fan-out; that loader is gone. We keep the same guard
  // for `loadMoreData` (scroll-driven pagination) so that an in-flight
  // page from the *previous* filter — fired when the user clicked a
  // chip mid-scroll — never gets appended on top of the freshly
  // refetched filter window. Without this, chip-flipping while the
  // table is loading can leave stale rows interleaved with the new
  // filter's results.
  const filterVersionRef = useRef(0);
  // Cached *unfiltered* contact total. Filtered fetches return a
  // filter-scoped `total_contacts` (correct for the table footer);
  // chip totals must always be against the whole campaign. We cache
  // the value from the last unfiltered fetch (or the
  // `unfiltered_total_contacts` sideload when one is present) and
  // expose it so the consumer can pass it to `compliance-summary`.
  const unfilteredTotalContactsRef = useRef(0);
  const toast = useToast();

  /**
   * Fetch data for a specific tab
   * 
   * @param {number} tabIndex - Tab index (0 for companies, 1 for contacts)
   * @param {number} offset - Pagination offset
   * @param {boolean} append - Whether to append to existing data
   */
  const fetchTabData = useCallback(async (tabIndex, offset = 0, append = false, searchTerm = null, complianceFilter = null) => {
    if (!campaignId) return;

    const nextSearch = typeof searchTerm === 'string'
      ? searchTerm.trim()
      : currentSearchRef.current;
    const normalizedSearch = nextSearch.length > 1 ? nextSearch : '';
    const searchChanged = normalizedSearch !== currentSearchRef.current;
    currentSearchRef.current = normalizedSearch;

    // Compliance filter changes should restart from the beginning too
    const nextFilter = complianceFilter !== null ? complianceFilter : currentComplianceFilterRef.current;
    const filterChanged = nextFilter !== currentComplianceFilterRef.current;
    currentComplianceFilterRef.current = nextFilter;

    if (searchChanged || filterChanged) {
      companiesOffsetRef.current = 0;
      contactsOffsetRef.current = 0;
      offset = 0;
      append = false;
      // Bump the filter version so any in-flight `loadMoreData` page
      // tied to the previous search/filter is silently discarded when
      // it returns (see the guard inside the contacts response handler
      // below). Without this, chip-flipping mid-scroll can interleave
      // stale rows.
      if (tabIndex === 1) filterVersionRef.current += 1;
    }
    
    setIsLoadingTabData(true);

    // Snapshot the version this fetch belongs to. If a chip-click
    // bumps `filterVersionRef` while this request is in flight, we
    // discard the response below — it would otherwise paint stale
    // rows from the previous filter on top of the active one.
    const myFilterVersion = filterVersionRef.current;

    try {
      const tabType = tabIndex === 0 ? 'companies' : 'contacts';
      const offsetRef = tabIndex === 0 ? companiesOffsetRef : contactsOffsetRef;
      const filterIsActive = Boolean(nextFilter && nextFilter !== 'all');

      const response = await campaignService.getCampaign(
        campaignId,
        offset,
        50,
        tabType,
        normalizedSearch,
        filterIsActive ? nextFilter : null
      );

      // Discard if the user changed search/filter mid-flight. We only
      // guard contacts (tab 1) since companies don't carry the chip UI.
      if (tabIndex === 1 && filterVersionRef.current !== myFilterVersion) {
        return;
      }

      // Always update version ID from the API response. Totals are
      // trickier — the tab labels ("Companies (805)", "Contacts
      // (1059)") are *whole-campaign* counts and must never be
      // overwritten with filter-scoped values. Two cases:
      //
      //  - Companies tab fetch, or unfiltered contacts fetch:
      //    response totals ARE the campaign totals — set them through.
      //  - Filtered contacts fetch: response.total_contacts is the
      //    filter-scoped count and response.total_companies is 0
      //    (the contacts path doesn't recompute companies). In this
      //    case we leave both `totalCompanies` and `totalContacts`
      //    pinned to the cached unfiltered values so the tab labels
      //    don't flip with chip clicks.
      if (response.pagination_info) {
        setActiveVersionId(response.pagination_info.version_id || null);

        if (tabIndex === 1 && filterIsActive) {
          // Refresh the unfiltered total cache if the backend sent
          // one (it does on the filtered path), and re-pin
          // `totalContacts` to that cached value so the tab label
          // stays "Contacts (1059)" rather than dropping to "(999)".
          const unfiltered = response.pagination_info.unfiltered_total_contacts;
          if (typeof unfiltered === 'number') {
            unfilteredTotalContactsRef.current = unfiltered;
            setTotalContacts(unfiltered);
          }
          // `totalCompanies` deliberately untouched — the compliance
          // filter only scopes contacts, so the previously-fetched
          // companies count is still authoritative.
        } else {
          setTotalCompanies(response.pagination_info.total_companies || 0);
          setTotalContacts(response.pagination_info.total_contacts || 0);
          if (tabIndex === 1) {
            unfilteredTotalContactsRef.current =
              response.pagination_info.total_contacts || 0;
          }
        }
      }
      
      if (tabIndex === 0) {
        // Companies data
        const companies = response.companies || [];
        const formattedCompanies = companies.map(company => ({
          ...company,
          id: company.elasticsearch_id || company.company_uuid || company.id
        }));
        
        if (append) {
          setCompaniesData(prev => [...prev, ...formattedCompanies]);
        } else {
          setCompaniesData(formattedCompanies);
        }
        
        // Initialize or clear field definitions from response
        if (response.field_definitions) {
          const companyFieldDefs = response.field_definitions.filter(def => def.entity_type === 'company');
          companyFields.initializeDefinitions(companyFieldDefs);
          contactFields.initializeDefinitions([]);
        } else if (!append) {
          companyFields.initializeDefinitions([]);
          contactFields.initializeDefinitions([]);
        }
        
        setHasMoreCompanies(response.pagination_info?.has_more || false);
        
        // Update offset based on items returned
        const itemsReturned = response.pagination_info?.returned || formattedCompanies.length;
        offsetRef.current = offset + itemsReturned;
      } else {
        // Contacts data TODO: look into this more. We need to stabalize custom field mapping
        const contacts = response.contacts || [];
        const formattedContacts = contacts.map(contact => ({
          ...contact,
          id: contact.elasticsearch_id || contact.id
        }));
        
        if (append) {
          // Dedup defense for Plan B Pattern A2 surgical removal:
          // when the user edits a row out of the active filter we
          // splice it from `contactsData` and decrement the offset
          // by 1 so the next page picks up the row we'd otherwise
          // skip. That's the right move ~99% of the time, but it
          // can occasionally produce a one-row overlap if the
          // server reorders mid-flight (rare). Cheap O(n+m) Set
          // makes the append idempotent regardless.
          setContactsData(prev => {
            const seen = new Set(prev.map(c => c.id));
            const fresh = formattedContacts.filter(c => !seen.has(c.id));
            return fresh.length === formattedContacts.length
              ? [...prev, ...formattedContacts]
              : [...prev, ...fresh];
          });
        } else {
          setContactsData(formattedContacts);
        }

        // Phase 3.5 compliance sideload. Every contacts response carries
        // these two keys (empty defaults when no reviewers exist), so we
        // can unconditionally adopt them — keeps state in sync with the
        // latest server computation of is_stale inside the decision
        // blobs attached to each contact's `custom_fields` map.
        setComplianceFieldMap(response.compliance_field_map || {});
        setHasComplianceReviewers(Boolean(response.has_compliance_reviewers));
        
        // Initialize or clear field definitions from response
        if (response.field_definitions) {
          const contactFieldDefs = response.field_definitions.filter(def => def.entity_type === 'contact');
          contactFields.initializeDefinitions(contactFieldDefs);
          companyFields.initializeDefinitions([]);
        } else if (!append) {
          contactFields.initializeDefinitions([]);
          companyFields.initializeDefinitions([]);
        }
        
        const hasMore = response.pagination_info?.has_more || false;
        setHasMoreContacts(hasMore);

        // Update offset based on items returned. The lazy 50-at-a-time
        // scroll loader (`loadMoreData`) takes over from here. The
        // previous eager `Promise.all` fan-out that loaded every
        // remaining page is gone — chip totals come from the
        // `/compliance-summary` endpoint, and chip-filter clicks
        // refetch from offset 0 with `compliance_filter` server-side,
        // so there's no longer any reason to keep the full dataset in
        // memory.
        const itemsReturned = response.pagination_info?.returned || formattedContacts.length;
        offsetRef.current = offset + itemsReturned;
      }
      
    } catch (error) {
      console.error('Error fetching tab data:', error);
      toast({
        title: 'Error loading data',
        description: error.response?.data?.detail || 'Failed to load campaign data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingTabData(false);
    }
  }, [campaignId, toast, companyFields, contactFields]);

  /**
   * Handle streaming data updates
   * Processes SSE events for companies, contacts, and custom fields
   * 
   * @param {Object} update - Update object from SSE stream
   * @param {number} activeTab - Active tab index (0 for companies, 1 for contacts)
   */
  const handleStreamingUpdate = useCallback((update, activeTab) => {
    // Handle reversion events - clear data and refresh
    if (update.type === 'reversion') {
      console.log('Reversion event detected - refreshing table data');
      setCompaniesData([]);
      setContactsData([]);
      setTotalCompanies(0);
      setTotalContacts(0);
      
      companyFields.initializeDefinitions([]);
      contactFields.initializeDefinitions([]);
      
      companiesOffsetRef.current = 0;
      contactsOffsetRef.current = 0;
      fetchTabData(activeTab, 0, false);
      
      return;
    }
    
    // Handle field definition events
    if (update.type === 'custom_field_definition') {
      const entityType = update.data.entity_type === 'company' ? 'companies' : 'contacts';
      if (entityType === 'companies') {
        companyFields.handleDefinitionUpdate(update.data);
      } else {
        contactFields.handleDefinitionUpdate(update.data);
      }
      return;
    }
    
    // Handle custom field value updates
    if (update.type === 'custom_field_update') {
      const valueUpdates = Array.isArray(update.data) ? update.data : [update.data];
      valueUpdates.forEach(valueUpdate => {
        if (valueUpdate.entity_type === 'company') {
          companyFields.handleValueUpdate(valueUpdate);
        } else if (valueUpdate.entity_type === 'contact') {
          contactFields.handleValueUpdate(valueUpdate);
        }
      });
      return;
    }
    
    // Handle companies data ready
    if (update.type === 'companies_data_ready') {
      const paginationInfo = update.data.pagination_info || {};
      const incomingCompanies = update.data.companies || [];
      const totalCompaniesFromEvent = paginationInfo.total_companies ?? update.data.count ?? 0;
      const isRemoval = paginationInfo.is_removal || false;
      
      setTotalCompanies(totalCompaniesFromEvent);
      
      if (paginationInfo.total_contacts !== undefined) {
        setTotalContacts(paginationInfo.total_contacts);
      }
      
      if (update.data.version_id) {
        setActiveVersionId(update.data.version_id);
      }
      
      // Initialize field definitions from streaming data if available
      if (update.data.field_definitions && update.data.field_definitions.length > 0) {
        const companyFieldDefs = update.data.field_definitions.filter(def => def.entity_type === 'company');
        companyFields.initializeDefinitions(companyFieldDefs);
        contactFields.initializeDefinitions([]);
      }
      
      const returnedCount = paginationInfo.returned ?? (incomingCompanies?.length ?? 0);
      const baseOffset = paginationInfo.offset ?? 0;
      const nextOffsetFromPayload = typeof paginationInfo.next_offset === 'number'
        ? paginationInfo.next_offset
        : null;
      const computedOffset = baseOffset + returnedCount;
      const expectedHasMore = paginationInfo.has_more ?? (totalCompaniesFromEvent > computedOffset);
      
      if (activeTab === 0) {
        setHasMoreCompanies(expectedHasMore);
        companiesOffsetRef.current = nextOffsetFromPayload ?? computedOffset;
      }
      
      // If this is a removal operation, REPLACE cache entirely instead of merging
      // This prevents stale cached data from persisting after removals
      if (isRemoval) {
        console.log('[useTableData] Removal detected - clearing companies cache and replacing with fresh data');
        
        if (incomingCompanies.length > 0) {
          const formattedCompanies = incomingCompanies.map(company => ({
            ...company,
            id: company.elasticsearch_id || company.company_uuid || company.id
          }));
          // Replace entire cache with fresh data
          setCompaniesData(formattedCompanies);
        } else {
          // No companies returned - clear cache
          setCompaniesData([]);
        }
        
        // Reset offset since we're starting fresh
        if (activeTab === 0) {
          companiesOffsetRef.current = computedOffset;
        }
        
        // Scroll to top after removal operation
        if (listRef?.current && activeTab === 0) {
          setTimeout(() => {
            listRef.current?.scrollToItem?.(0, 'start');
          }, 0);
        }
      } else {
        // Normal merge behavior for additions
        if (incomingCompanies.length > 0) {
          const formattedCompanies = incomingCompanies.map(company => ({
            ...company,
            id: company.elasticsearch_id || company.company_uuid || company.id
          }));
          
          setCompaniesData(prev => {
            const merged = [];
            const seen = new Set();
            
            formattedCompanies.forEach(company => {
              if (!seen.has(company.id)) {
                seen.add(company.id);
                merged.push(company);
              }
            });
            
            prev.forEach(company => {
              if (!seen.has(company.id)) {
                seen.add(company.id);
                merged.push(company);
              }
            });
            
            return merged;
          });
        } else if (paginationInfo && paginationInfo.offset === 0 && (paginationInfo.returned ?? 0) === 0) {
          setCompaniesData([]);
        } else if (totalCompaniesFromEvent === 0) {
          setCompaniesData([]);
        }
      }
    } 
    
    // Handle contacts data ready
    else if (update.type === 'contacts_data_ready') {
      const paginationInfo = update.data.pagination_info || {};
      const incomingContacts = update.data.contacts || [];
      const totalContactsFromEvent = paginationInfo.total_contacts ?? update.data.count ?? 0;
      const isRemoval = paginationInfo.is_removal || false;
      
      setTotalContacts(totalContactsFromEvent);
      
      if (paginationInfo.total_companies !== undefined) {
        setTotalCompanies(paginationInfo.total_companies);
      }
      
      if (update.data.version_id) {
        setActiveVersionId(update.data.version_id);
      }
      
      // Initialize field definitions from streaming data if available
      if (update.data.field_definitions && update.data.field_definitions.length > 0) {
        const contactFieldDefs = update.data.field_definitions.filter(def => def.entity_type === 'contact');
        contactFields.initializeDefinitions(contactFieldDefs);
        companyFields.initializeDefinitions([]);
      }
      
      const returnedCount = paginationInfo.returned ?? (incomingContacts?.length ?? 0);
      const baseOffset = paginationInfo.offset ?? 0;
      const nextOffsetFromPayload = typeof paginationInfo.next_offset === 'number'
        ? paginationInfo.next_offset
        : null;
      const computedOffset = baseOffset + returnedCount;
      const expectedHasMore = paginationInfo.has_more ?? (totalContactsFromEvent > computedOffset);
      
      if (activeTab === 1) {
        setHasMoreContacts(expectedHasMore);
        contactsOffsetRef.current = nextOffsetFromPayload ?? computedOffset;
      }
      
      // If this is a removal operation, REPLACE cache entirely instead of merging
      // This prevents stale cached data from persisting after removals
      if (isRemoval) {
        console.log('[useTableData] Removal detected - clearing contacts cache and replacing with fresh data');
        
        if (incomingContacts.length > 0) {
          const formattedContacts = incomingContacts.map(contact => ({
            ...contact,
            id: contact.elasticsearch_id || contact.id
          }));
          // Replace entire cache with fresh data
          setContactsData(formattedContacts);
        } else {
          // No contacts returned - clear cache
          setContactsData([]);
        }
        
        // Reset offset since we're starting fresh
        if (activeTab === 1) {
          contactsOffsetRef.current = computedOffset;
        }
        
        // Scroll to top after removal operation
        if (listRef?.current && activeTab === 1) {
          setTimeout(() => {
            listRef.current?.scrollToItem?.(0, 'start');
          }, 0);
        }
      } else {
        // Normal merge behavior for additions
        if (incomingContacts.length > 0) {
          const formattedContacts = incomingContacts.map(contact => ({
            ...contact,
            id: contact.elasticsearch_id || contact.id
          }));
          
          setContactsData(prev => {
            const merged = [];
            const seen = new Set();
            
            formattedContacts.forEach(contact => {
              if (!seen.has(contact.id)) {
                seen.add(contact.id);
                merged.push(contact);
              }
            });
            
            prev.forEach(contact => {
              if (!seen.has(contact.id)) {
                seen.add(contact.id);
                merged.push(contact);
              }
            });
            
            return merged;
          });
        } else if (paginationInfo && paginationInfo.offset === 0 && (paginationInfo.returned ?? 0) === 0) {
          setContactsData([]);
        } else if (totalContactsFromEvent === 0) {
          setContactsData([]);
        }
      }
    }
  }, [companyFields, contactFields, fetchTabData, toast]);

  /**
   * Load more data for pagination
   * 
   * @param {number} activeTab - Active tab index
   */
  const loadMoreData = useCallback((activeTab) => {
    const hasMore = activeTab === 0 ? hasMoreCompanies : hasMoreContacts;
    const offsetRef = activeTab === 0 ? companiesOffsetRef : contactsOffsetRef;
    
    if (hasMore && !isLoadingTabData) {
      // Preserve the currently active compliance filter when paginating
      fetchTabData(activeTab, offsetRef.current, true, currentSearchRef.current, currentComplianceFilterRef.current);
    }
  }, [hasMoreCompanies, hasMoreContacts, isLoadingTabData, fetchTabData, currentComplianceFilterRef]);

  /**
   * Plan B Pattern A2 — surgically remove a single contact row from
   * the local table without a full refetch. Used by the edit handlers
   * in `VirtualizedPersonaResults.jsx` when an edit causes a row to
   * stop matching the active compliance chip filter (Linear-style
   * "row vanishes instantly").
   *
   * Behavior:
   *  - No-op if the row isn't in `contactsData` (already removed,
   *    not yet loaded, or wrong page).
   *  - Decrements `contactsOffsetRef` by 1 because the next scroll
   *    fetch will start from a lower index in the (now shorter)
   *    server-side filtered list. Without the decrement we'd skip a
   *    row each time the user editted-and-scrolled.
   *  - Returns true when a row was actually removed; callers don't
   *    use the boolean today, but it's there for future toast/animation
   *    hooks ("Removed N rows from this filter").
   *
   * Server-side `total_contacts` and chip counts are refreshed by the
   * caller's existing `queryClient.invalidateQueries(['complianceSummary'])`
   * call — this helper deliberately doesn't touch `totalContacts` so
   * the tab label "Contacts (1059)" stays at its unfiltered value.
   */
  const removeContactRow = useCallback((membershipId) => {
    if (!membershipId) return false;
    const targetId = String(membershipId);
    let didRemove = false;
    setContactsData(prev => {
      const next = prev.filter(c => String(c.membership_id) !== targetId);
      if (next.length === prev.length) return prev;
      didRemove = true;
      return next;
    });
    if (didRemove && contactsOffsetRef.current > 0) {
      contactsOffsetRef.current -= 1;
    }
    return didRemove;
  }, []);

  return {
    companiesData,
    contactsData,
    totalCompanies,
    totalContacts,
    isLoadingTabData,
    hasMoreCompanies,
    hasMoreContacts,
    activeVersionId,
    setCompaniesData,
    setContactsData,
    setTotalCompanies,
    setTotalContacts,
    fetchTabData,
    handleStreamingUpdate,
    loadMoreData,
    removeContactRow,
    companiesOffsetRef,
    contactsOffsetRef,
    currentSearchRef,
    currentComplianceFilterRef,
    // Phase 3.5 — advisor compliance sideload from the last contacts fetch
    complianceFieldMap,
    hasComplianceReviewers,
    // Plan A — unfiltered contact total cached across filter flips so
    // the chip-summary endpoint always receives the whole-campaign
    // total to multiply by `num_drafts`. Reading the ref via a getter
    // keeps callers from accidentally subscribing to it as state.
    getUnfilteredTotalContacts: () => unfilteredTotalContactsRef.current,
  };
};

export default useTableData;

